/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useFormatter, useTranslations } from "@i18n/react";
import type { TriviaGame, TriviaQuestion } from "@packages/sdk";
import type { JSX } from "react";

import {
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../../../global/emotion";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColor } from "../../../../../utils/colors/types/CSSColor.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import type { ButtonProps } from "../../../_core/Button/Button.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../icons/Icon/GeneralIcons";
import { CloseIcon } from "../../../icons/Icon/NavigationIcons";

const hallowElement: HallowElement = "div";

const actionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
  zIndex: 1,
};
const containerStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  width: spacing.fit,
};
const iconContainerStyle: Interpolation<{ size: number }> = (props) => ({
  alignItems: "center",
  borderRadius: radius.full,
  display: "flex",
  height: props.size,
  justifyContent: "center",
  minHeight: props.size,
  minWidth: props.size,
  width: props.size,
});
const iconContainerCorrectStyle: Interpolation = {
  backgroundColor: "#00BB8C",
};
const iconContainerIncorrectStyle: Interpolation = {
  backgroundColor: "#FF4762",
};
const scoresContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  gap: "15px",
  justifyContent: "center",
};
const summaryContainerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "space-around",
  paddingInline: "15px",
  width: spacing.full,
};
const summaryStatisticContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  textAlign: "center",
};
const summaryStatisticLabelStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  whiteSpace: "nowrap",
};

export type TriviaScoreProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  actions?: JSX.Element[];
  color?: CSSColor;
  questions: Pick<TriviaQuestion, "user_answer" | "answers" | "id">[];
  shareButtonProps?: ButtonProps;
  size?: "m" | "l";
  summary: TriviaGame["summary"];
};

interface TriviaAnswerScoreProps {
  correct: boolean;
  questionId: number;
  index: number;
  size: TriviaScoreProps["size"];
}

const TriviaAnswerScore = ({
  correct,
  questionId,
  index,
  size,
}: TriviaAnswerScoreProps) => {
  const Icon = correct ? CheckmarkIcon.SimpleOn : CloseIcon;
  return (
    <div
      key={`${questionId}${index}`}
      css={[
        iconContainerStyle({ size: size === "l" ? 48 : 32 }),
        correct ? iconContainerCorrectStyle : iconContainerIncorrectStyle,
      ]}
    >
      <Icon
        size={size === "l" ? 30 : 20}
        fill={constantSemanticColors.onColorHighest}
      />
    </div>
  );
};

export const TriviaScore = ({
  actions,
  asChild = false,
  children,
  color,
  questions,
  size = "m",
  summary,
  ...props
}: TriviaScoreProps) => {
  const format = useFormatter();
  const t = useTranslations();

  const userAnswers = questions.map((question) => {
    const correctAnswer = question.answers.find((answer) => answer.correct);
    const userAnswer = question.user_answer;

    return {
      correct: correctAnswer?.id === userAnswer?.answer_id,
      questionId: question.id,
    };
  });

  const DeterminedElement = determineElementFromAsChild({
    asChild,
    hallowElement,
  });

  const DeterminedChildren = determineChildrenInject({
    afterChildrenInject: [
      <Text
        key="text"
        type={size === "l" ? "headline" : "title"}
        size={size === "l" ? "m" : "l"}
      >
        {t("trivia_your_score")}
      </Text>,
      <div key="scoresContainer" css={scoresContainerStyle}>
        {userAnswers.map((userAnswer, index) => (
          <TriviaAnswerScore
            key={`${userAnswer.questionId}_${index}`}
            {...userAnswer}
            index={index}
            size={size}
          />
        ))}
      </div>,
      <div key="summaryContainer" css={summaryContainerStyle}>
        {[
          {
            label: t("trivia_summary_seconds"),
            value: format.number(summary.total_seconds_elapsed),
          },
          {
            label: t("trivia_current_streak"),
            value: format.number(summary.streak),
          },
          {
            label: t("trivia_max_streak"),
            value: format.number(summary.longest_streak),
          },
        ].map((statistic) => (
          <div key={statistic.label} css={summaryStatisticContainerStyle}>
            <Text
              type={size === "l" ? "headline" : "title"}
              size={size === "l" ? "m" : "l"}
            >
              {statistic.value}
            </Text>
            <Text type="detail" size="xl" css={summaryStatisticLabelStyle}>
              {statistic.label}
            </Text>
          </div>
        ))}
      </div>,
      actions?.length > 0 ? (
        <div key="actions" css={actionsStyle}>
          {actions}
        </div>
      ) : null,
    ],
    children: children as JSX.Element,
  });

  return (
    <DeterminedElement css={containerStyle} {...props}>
      {DeterminedChildren}
    </DeterminedElement>
  );
};
